import React, { useState, useEffect } from "react";
import {
  UtensilsCrossed,
  AlertCircle,
  Clock3,
  Save,
  Coffee,
  Sun,
  Apple,
  RotateCcw
} from "lucide-react";
import HealthGoals from "./HealthGoals";
import { getProfileStepDetails, updateProfileStep } from "../../api/api";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${type === "error" ? "bg-red-500" : type === "success" ? "bg-green-500" : "bg-blue-500"
      } text-white min-w-[300px]`}>
      <span className="text-sm flex-1">{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200 text-xl">×</button>
    </div>
  );
};

const DietaryPreferences = ({ setActiveComponent }) => {
  const [toasts, setToasts] = useState([]);
  const [activeScreen, setActiveScreen] = useState("dietaryPreferences");
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  const [dietaryData, setDietaryData] = useState({
    foodHabits: [],
    foodCulture: [],
    foodCultureOther: "",
    snackingHabit: false,
    snacksDetails: "",
    eatOutsideFood: false,
    outsideFoodFrequency: "",
    teaConsumption: false,
    teaCupsPerDay: "",
    coffeeConsumption: false,
    coffeeCupsPerDay: "",
    softDrinksConsumption: false,
    softDrinksFrequency: "",
    sweetsConsumption: "",
    cookingOil: "",
    cookingOilOther: "",
    breakfastTime: "",
    breakfastMenu: "",
    lunchTime: "",
    lunchMenu: "",
    eveningSnackTime: "",
    eveningSnackMenu: "",
    dinnerTime: "",
    dinnerMenu: "",
    postDinnerConsumption: "",
    snackAfterDinner: false,
    afterDinnerSnacks: "",
    packagedFoodsConsumption: false,
    packagedFoodsDetails: "",
    weekendEatingPattern: false,
    weekendEatingDetails: "",
    foodSensitivities: false,
    foodSensitivitiesDetails: "",
    foodAllergies: false,
    foodAllergiesDetails: ""
  });

  const [formErrors, setFormErrors] = useState({});

  const foodHabitsOptions = ["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan", "Jain", "Can eat fish"];

  const foodCultureOptions = [
    "North Indian", "South Indian", "Gujrati", "Rajasthani", "Goa", "Maharashtra",
    "Punjab", "Bangoli", "Bihar", "Odisa", "Chinese", "Japanese", "Korean",
    "Thailand", "Mediterranean", "French", "Italian", "American", "African",
    "European", "Other"
  ];

  const cookingOilOptions = [
    "Mustard oil", "Groundnut oil", "Olive oil", "Ghee", "Sunflower oil",
    "Coconut oil", "Canola oil", "Soybean oil", "Rice bran oil", "Safflower oil",
    "Sesame oil", "Peanut oil", "Cottonseed oil", "Palm oil", "Avocado oil",
    "Flaxseed oil", "Walnut oil", "Pumpkin seed oil", "Chia oil", "Hemp seed oil", "Other"
  ];

  const frequencyOptions = ["Once a week", "2–3 times a week", "Daily", "Occasionally", "Never"];
  const sweetsOptions = ["Occasionally", "Often", "Daily"];

  // Generate time options for dropdowns
  const generateTimeOptions = (startHour = 0, endHour = 23) => {
    const options = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const displayTime = `${displayHour}:${minuteStr} ${ampm}`;
        const valueTime = `${hourStr}:${minuteStr}`;
        options.push({ display: displayTime, value: valueTime });
      }
    }
    return options;
  };

  // Breakfast: 12:00 AM to 12:00 PM (noon) - Morning only
  const breakfastTimeOptions = generateTimeOptions(0, 12);
  // Lunch: All 24 hours
  const lunchTimeOptions = generateTimeOptions(0, 23);
  // Evening Snack: 12:00 PM to 11:30 PM (Afternoon to night)
  const eveningSnackTimeOptions = generateTimeOptions(12, 23);
  // Dinner: 12:00 PM to 11:30 PM (Afternoon to night)
  const dinnerTimeOptions = generateTimeOptions(12, 23);

  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    fetchDietaryData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchDietaryData = async () => {
    try {
      setLoading(true);
      const res = await getProfileStepDetails();

      if (res?.status) {
        const dietaryFields = res?.profile_completion?.steps?.dietary?.fields;

        const fieldMap = {};
        dietaryFields.forEach((item) => {
          fieldMap[item.name] = item.value;
        });

        console.log("📋 Fetched Dietary Data:", fieldMap);

        let foodHabitsArray = [];
        if (fieldMap.foodHabits) {
          if (typeof fieldMap.foodHabits === 'string') {
            foodHabitsArray = fieldMap.foodHabits.split(',').map(s => s.trim());
          } else if (Array.isArray(fieldMap.foodHabits)) {
            foodHabitsArray = fieldMap.foodHabits;
          }
        }

        let foodCultureArray = [];
        if (fieldMap.foodCulture) {
          if (typeof fieldMap.foodCulture === 'string') {
            foodCultureArray = fieldMap.foodCulture.split(',').map(s => s.trim());
          } else if (Array.isArray(fieldMap.foodCulture)) {
            foodCultureArray = fieldMap.foodCulture;
          }
        }

        setDietaryData({
          foodHabits: foodHabitsArray,
          foodCulture: foodCultureArray,
          foodCultureOther: "",
          snackingHabit: fieldMap.snackingHabit || false,
          snacksDetails: fieldMap.snacksDetails || "",
          eatOutsideFood: fieldMap.eatOutsideFood || false,
          outsideFoodFrequency: fieldMap.outsideFoodFrequency || "",
          teaConsumption: fieldMap.teaConsumption || false,
          teaCupsPerDay: fieldMap.teaCupsPerDay || "",
          coffeeConsumption: fieldMap.coffeeConsumption || false,
          coffeeCupsPerDay: fieldMap.coffeeCupsPerDay || "",
          softDrinksConsumption: fieldMap.softDrinksConsumption || false,
          softDrinksFrequency: fieldMap.softDrinksFrequency || "",
          sweetsConsumption: fieldMap.sweetsConsumption || "",
          cookingOil: fieldMap.cookingOil || "",
          cookingOilOther: "",
          breakfastTime: fieldMap.breakfastTime || "",
          breakfastMenu: fieldMap.breakfastMenu || "",
          lunchTime: fieldMap.lunchTime || "",
          lunchMenu: fieldMap.lunchMenu || "",
          eveningSnackTime: fieldMap.eveningSnackTime || "",
          eveningSnackMenu: fieldMap.eveningSnackMenu || "",
          dinnerTime: fieldMap.dinnerTime || "",
          dinnerMenu: fieldMap.dinnerMenu || "",
          postDinnerConsumption: fieldMap.postDinnerConsumption || "",
          snackAfterDinner: fieldMap.snackAfterDinner || false,
          afterDinnerSnacks: fieldMap.afterDinnerSnacks || "",
          packagedFoodsConsumption: fieldMap.packagedFoodsConsumption || false,
          packagedFoodsDetails: fieldMap.packagedFoodsDetails || "",
          weekendEatingPattern: fieldMap.weekendEatingPattern || false,
          weekendEatingDetails: fieldMap.weekendEatingDetails || "",
          foodSensitivities: fieldMap.foodSensitivities || false,
          foodSensitivitiesDetails: fieldMap.foodSensitivitiesDetails || "",
          foodAllergies: fieldMap.foodAllergies || false,
          foodAllergiesDetails: fieldMap.foodAllergiesDetails || ""
        });
      }
    } catch (error) {
      console.error("Error fetching dietary data:", error);
      addToast("Failed to load dietary data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setDietaryData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleFoodHabitsToggle = (option) => {
    setDietaryData(prev => {
      const current = [...prev.foodHabits];
      if (current.includes(option)) {
        return { ...prev, foodHabits: current.filter(item => item !== option) };
      } else {
        return { ...prev, foodHabits: [...current, option] };
      }
    });
    if (formErrors.foodHabits) {
      setFormErrors(prev => ({ ...prev, foodHabits: "" }));
    }
  };

  const handleSelectAllFoodHabits = () => {
    if (dietaryData.foodHabits.length === foodHabitsOptions.length) {
      setDietaryData(prev => ({ ...prev, foodHabits: [] }));
    } else {
      setDietaryData(prev => ({ ...prev, foodHabits: [...foodHabitsOptions] }));
    }
    if (formErrors.foodHabits) {
      setFormErrors(prev => ({ ...prev, foodHabits: "" }));
    }
  };

  const handleFoodCultureToggle = (option) => {
    setDietaryData(prev => {
      const current = [...prev.foodCulture];
      if (current.includes(option)) {
        return { ...prev, foodCulture: current.filter(item => item !== option) };
      } else {
        return { ...prev, foodCulture: [...current, option] };
      }
    });
    if (formErrors.foodCulture) {
      setFormErrors(prev => ({ ...prev, foodCulture: "" }));
    }
  };

  const handleSelectAllFoodCulture = () => {
    if (dietaryData.foodCulture.length === foodCultureOptions.length) {
      setDietaryData(prev => ({ ...prev, foodCulture: [] }));
    } else {
      setDietaryData(prev => ({ ...prev, foodCulture: [...foodCultureOptions] }));
    }
    if (formErrors.foodCulture) {
      setFormErrors(prev => ({ ...prev, foodCulture: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (dietaryData.foodHabits.length === 0) errors.foodHabits = "Please select at least one food habit";
    if (dietaryData.foodCulture.length === 0) errors.foodCulture = "Please select at least one food culture";

    if (dietaryData.snackingHabit && !dietaryData.snacksDetails?.trim()) {
      errors.snacksDetails = "Please specify what you eat";
    }

    if (dietaryData.eatOutsideFood && !dietaryData.outsideFoodFrequency) {
      errors.outsideFoodFrequency = "Please select frequency";
    }

    if (dietaryData.teaConsumption && !dietaryData.teaCupsPerDay) {
      errors.teaCupsPerDay = "Please enter number of cups";
    }

    if (dietaryData.coffeeConsumption && !dietaryData.coffeeCupsPerDay) {
      errors.coffeeCupsPerDay = "Please enter number of cups";
    }

    if (dietaryData.softDrinksConsumption && !dietaryData.softDrinksFrequency) {
      errors.softDrinksFrequency = "Please select frequency";
    }

    if (!dietaryData.sweetsConsumption) errors.sweetsConsumption = "Please select sweets consumption";
    if (!dietaryData.cookingOil) errors.cookingOil = "Please select cooking oil";

    if (dietaryData.snackAfterDinner && !dietaryData.afterDinnerSnacks?.trim()) {
      errors.afterDinnerSnacks = "Please specify what you consume";
    }

    if (dietaryData.packagedFoodsConsumption && !dietaryData.packagedFoodsDetails?.trim()) {
      errors.packagedFoodsDetails = "Please specify what you eat";
    }

    if (dietaryData.weekendEatingPattern && !dietaryData.weekendEatingDetails?.trim()) {
      errors.weekendEatingDetails = "Please specify how it's different";
    }

    if (dietaryData.foodSensitivities && !dietaryData.foodSensitivitiesDetails?.trim()) {
      errors.foodSensitivitiesDetails = "Please mention food sensitivities";
    }
    if (dietaryData.foodAllergies && !dietaryData.foodAllergiesDetails?.trim()) {
      errors.foodAllergiesDetails = "Please mention food allergies";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (validateForm()) {
      setIsSaving(true);

      try {
        const dietaryPayload = {
          foodHabits: dietaryData.foodHabits.length > 0 ? dietaryData.foodHabits.join(", ") : null,
          foodCulture: dietaryData.foodCulture.length > 0 ? dietaryData.foodCulture.join(", ") : null,
          snackingHabit: dietaryData.snackingHabit || false,
          snacksDetails: dietaryData.snacksDetails?.trim() || null,
          eatOutsideFood: dietaryData.eatOutsideFood || false,
          outsideFoodFrequency: dietaryData.outsideFoodFrequency || null,
          teaConsumption: dietaryData.teaConsumption || false,
          teaCupsPerDay: dietaryData.teaCupsPerDay ? parseInt(dietaryData.teaCupsPerDay) : null,
          coffeeConsumption: dietaryData.coffeeConsumption || false,
          coffeeCupsPerDay: dietaryData.coffeeCupsPerDay ? parseInt(dietaryData.coffeeCupsPerDay) : null,
          softDrinksConsumption: dietaryData.softDrinksConsumption || false,
          softDrinksFrequency: dietaryData.softDrinksFrequency || null,
          sweetsConsumption: dietaryData.sweetsConsumption || null,
          cookingOil: dietaryData.cookingOil || null,
          breakfastTime: dietaryData.breakfastTime || null,
          breakfastMenu: dietaryData.breakfastMenu?.trim() || null,
          lunchTime: dietaryData.lunchTime || null,
          lunchMenu: dietaryData.lunchMenu?.trim() || null,
          eveningSnackTime: dietaryData.eveningSnackTime || null,
          eveningSnackMenu: dietaryData.eveningSnackMenu?.trim() || null,
          dinnerTime: dietaryData.dinnerTime || null,
          dinnerMenu: dietaryData.dinnerMenu?.trim() || null,
          postDinnerConsumption: dietaryData.postDinnerConsumption?.trim() || null,
          snackAfterDinner: dietaryData.snackAfterDinner || false,
          afterDinnerSnacks: dietaryData.afterDinnerSnacks?.trim() || null,
          packagedFoodsConsumption: dietaryData.packagedFoodsConsumption || false,
          packagedFoodsDetails: dietaryData.packagedFoodsDetails?.trim() || null,
          weekendEatingPattern: dietaryData.weekendEatingPattern || false,
          weekendEatingDetails: dietaryData.weekendEatingDetails?.trim() || null,
          foodSensitivities: dietaryData.foodSensitivities || false,
          foodSensitivitiesDetails: dietaryData.foodSensitivitiesDetails?.trim() || null,
          foodAllergies: dietaryData.foodAllergies || false,
          foodAllergiesDetails: dietaryData.foodAllergiesDetails?.trim() || null
        };

        console.log("📤 Sending Dietary Payload:", dietaryPayload);

        const response = await updateProfileStep("dietary", dietaryPayload);

        console.log("📥 Dietary Save Response:", response);

        if (response?.status) {
          addToast("Dietary preferences saved successfully!", "success");
          await fetchDietaryData();

          setTimeout(() => {
            if (setActiveComponent) {
              setActiveComponent("healthGoals");
            }
          }, 500);
        } else {
          addToast(response?.message || "Failed to save data", "error");
        }
      } catch (error) {
        console.error("Save error:", error);
        addToast(error.message || "Failed to save dietary preferences", "error");
      } finally {
        setIsSaving(false);
      }
    } else {
      addToast("Please fill in all required fields", "error");
      const firstErrorField = Object.keys(formErrors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
    }
  };

  const handleDiscard = () => {
    fetchDietaryData();
    setFormErrors({});
    addToast("All changes discarded", "info");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
      </div>
    );
  }

  if (activeScreen === "healthGoals") {
    return <HealthGoals />;
  }

  return (
    <div>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
          Dietary Preferences
        </h2>
        <p className="text-[#98a19b] text-sm">
          Tailor your nutrition plan by updating your dietary restrictions and preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Food Habits & Culture */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <UtensilsCrossed size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Food Habits & Culture
              </h3>
            </div>

            {/* Food Habits - Custom Dropdown with Checkboxes */}
            <div className="mb-4 relative" ref={dropdownRef}>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Food Habits <span className="text-red-500">*</span>
              </label>
              
              {/* Dropdown Trigger */}
              <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none flex items-center justify-between cursor-pointer bg-white ${formErrors.foodHabits ? 'border-red-500' : 'border-[#e1e6e1]'}`}
              >
                <span className="text-[#24312d]">
                  {dietaryData.foodHabits.length > 0 
                    ? `${dietaryData.foodHabits.length} selected` 
                    : "Select Food Habits"}
                </span>
                <svg className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Dropdown Options */}
              {isDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-[#e1e6e1] rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {/* Select All Option */}
                  <div className="sticky top-0 bg-gray-50 border-b border-[#e1e6e1] p-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dietaryData.foodHabits.length === foodHabitsOptions.length}
                        onChange={handleSelectAllFoodHabits}
                        className="w-4 h-4 accent-green-500"
                      />
                      <span className="text-sm font-medium text-[#24312d]">Select All</span>
                    </label>
                  </div>
                  
                  {foodHabitsOptions.map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={dietaryData.foodHabits.includes(opt)}
                        onChange={() => handleFoodHabitsToggle(opt)}
                        className="w-4 h-4 accent-green-500"
                      />
                      <span className="text-sm text-[#24312d]">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              
              {formErrors.foodHabits && (
                <p className="text-red-500 text-xs mt-1">{formErrors.foodHabits}</p>
              )}
            </div>

            {/* Food Culture - Grid with Checkboxes */}
            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Food Culture / Cuisine <span className="text-red-500">*</span>
              </label>
              <div className={`grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md ${formErrors.foodCulture ? 'border-red-500' : 'border-[#e1e6e1]'}`}>
                {/* Select All for Food Culture */}
                <div className="col-span-2 border-b border-[#e1e6e1] pb-2 mb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dietaryData.foodCulture.length === foodCultureOptions.length}
                      onChange={handleSelectAllFoodCulture}
                      className="w-4 h-4 accent-green-500"
                    />
                    <span className="text-sm font-medium text-[#24312d]">Select All</span>
                  </label>
                </div>
                
                {foodCultureOptions.map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dietaryData.foodCulture.includes(opt)}
                      onChange={() => handleFoodCultureToggle(opt)}
                      className="w-4 h-4 accent-green-500"
                    />
                    <span className="text-[13px] text-[#24312d]">{opt}</span>
                  </label>
                ))}
              </div>
              {formErrors.foodCulture && (
                <p className="text-red-500 text-xs mt-1">{formErrors.foodCulture}</p>
              )}
            </div>

            {dietaryData.foodCulture.includes("Other") && (
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Mention Other Cuisine
                </label>
                <input
                  type="text"
                  value={dietaryData.foodCultureOther}
                  onChange={(e) => handleInputChange("foodCultureOther", e.target.value)}
                  placeholder="Please specify"
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
                />
              </div>
            )}
          </div>

          {/* Food Sensitivities */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle size={18} className="text-yellow-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Food Sensitivities
              </h3>
            </div>

            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] text-[#24312d]">
                Do you have any food sensitivities?
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={dietaryData.foodSensitivities}
                  onChange={(e) => handleInputChange("foodSensitivities", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                <span className="ml-3 text-sm text-[#24312d]">
                  {dietaryData.foodSensitivities ? "Yes" : "No"}
                </span>
              </label>
            </div>

            {dietaryData.foodSensitivities && (
              <div className="mt-2">
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Please specify your food sensitivities <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="foodSensitivitiesDetails"
                  value={dietaryData.foodSensitivitiesDetails}
                  onChange={(e) => handleInputChange("foodSensitivitiesDetails", e.target.value)}
                  rows="2"
                  placeholder="e.g., Gluten, Lactose, Dairy, etc."
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 ${formErrors.foodSensitivitiesDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
                {formErrors.foodSensitivitiesDetails && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.foodSensitivitiesDetails}</p>
                )}
              </div>
            )}
          </div>

          {/* Food Allergies */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle size={18} className="text-red-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Food Allergies
              </h3>
            </div>

            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] text-[#24312d]">
                Do you have any food allergies?
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={dietaryData.foodAllergies}
                  onChange={(e) => handleInputChange("foodAllergies", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                <span className="ml-3 text-sm text-[#24312d]">
                  {dietaryData.foodAllergies ? "Yes" : "No"}
                </span>
              </label>
            </div>

            {dietaryData.foodAllergies && (
              <div className="mt-2">
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Please specify your food allergies <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="foodAllergiesDetails"
                  value={dietaryData.foodAllergiesDetails}
                  onChange={(e) => handleInputChange("foodAllergiesDetails", e.target.value)}
                  rows="2"
                  placeholder="e.g., Peanuts, Shellfish, Eggs, etc."
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 ${formErrors.foodAllergiesDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
                {formErrors.foodAllergiesDetails && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.foodAllergiesDetails}</p>
                )}
              </div>
            )}
          </div>

          {/* Meal Times */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock3 size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Meal Times
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Breakfast Time - Morning only (12 AM to 12 PM) */}
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Breakfast Time
                </label>
                <select
                  value={dietaryData.breakfastTime}
                  onChange={(e) => handleInputChange("breakfastTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a] bg-white"
                >
                  <option value="">Select Time</option>
                  {breakfastTimeOptions.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.display}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Breakfast Menu
                </label>
                <textarea
                  value={dietaryData.breakfastMenu}
                  onChange={(e) => handleInputChange("breakfastMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., Poha with peas and peanuts + 1 cup tea"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>

              {/* Lunch Time - All 24 hours */}
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Lunch Time
                </label>
                <select
                  value={dietaryData.lunchTime}
                  onChange={(e) => handleInputChange("lunchTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a] bg-white"
                >
                  <option value="">Select Time</option>
                  {lunchTimeOptions.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.display}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Lunch Menu
                </label>
                <textarea
                  value={dietaryData.lunchMenu}
                  onChange={(e) => handleInputChange("lunchMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., 2 rotis + dal + sabzi + salad"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>

              {/* Evening Snack Time - PM only (12 PM to 11:30 PM) */}
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Evening Snack Time
                </label>
                <select
                  value={dietaryData.eveningSnackTime}
                  onChange={(e) => handleInputChange("eveningSnackTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a] bg-white"
                >
                  <option value="">Select Time</option>
                  {eveningSnackTimeOptions.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.display}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Snacks Menu
                </label>
                <textarea
                  value={dietaryData.eveningSnackMenu}
                  onChange={(e) => handleInputChange("eveningSnackMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., 1 apple + 4 almonds / green tea"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>

              {/* Dinner Time - PM only (12 PM to 11:30 PM) */}
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Dinner Time
                </label>
                <select
                  value={dietaryData.dinnerTime}
                  onChange={(e) => handleInputChange("dinnerTime", e.target.value)}
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a] bg-white"
                >
                  <option value="">Select Time</option>
                  {dinnerTimeOptions.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.display}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Dinner Menu
                </label>
                <textarea
                  value={dietaryData.dinnerMenu}
                  onChange={(e) => handleInputChange("dinnerMenu", e.target.value)}
                  rows="2"
                  placeholder="e.g., 1 bowl soup + 1 roti + paneer curry"
                  className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Post-Dinner Drink / Dessert
              </label>
              <input
                type="text"
                value={dietaryData.postDinnerConsumption}
                onChange={(e) => handleInputChange("postDinnerConsumption", e.target.value)}
                placeholder="e.g., 1 cup chamomile tea / warm milk"
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              />
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Do you snack after dinner?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.snackAfterDinner}
                    onChange={(e) => handleInputChange("snackAfterDinner", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.snackAfterDinner ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.snackAfterDinner && (
                <textarea
                  name="afterDinnerSnacks"
                  value={dietaryData.afterDinnerSnacks}
                  onChange={(e) => handleInputChange("afterDinnerSnacks", e.target.value)}
                  rows="2"
                  placeholder="What do you consume?"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.afterDinnerSnacks
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Snacking & Eating Habits */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Apple size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Snacking & Eating Habits
              </h3>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Do you have a habit of snacking between meals?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.snackingHabit}
                    onChange={(e) => handleInputChange("snackingHabit", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.snackingHabit ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.snackingHabit && (
                <textarea
                  name="snacksDetails"
                  value={dietaryData.snacksDetails}
                  onChange={(e) => handleInputChange("snacksDetails", e.target.value)}
                  rows="2"
                  placeholder="What do you eat? (e.g., Nuts, roasted chana, biscuits)"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.snacksDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Do you eat outside food?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.eatOutsideFood}
                    onChange={(e) => handleInputChange("eatOutsideFood", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.eatOutsideFood ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.eatOutsideFood && (
                <select
                  name="outsideFoodFrequency"
                  value={dietaryData.outsideFoodFrequency}
                  onChange={(e) => handleInputChange("outsideFoodFrequency", e.target.value)}
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.outsideFoodFrequency
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                >
                  <option value="" disabled>How often?</option>
                  {frequencyOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Beverages */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Coffee size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Beverages
              </h3>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">Do you drink Tea?</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.teaConsumption}
                    onChange={(e) => handleInputChange("teaConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.teaConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.teaConsumption && (
                <input
                  type="text"
                  name="teaCupsPerDay"
                  value={dietaryData.teaCupsPerDay}
                  onChange={(e) => handleInputChange("teaCupsPerDay", e.target.value)}
                  placeholder="How many cups daily? (e.g., 3)"
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.teaCupsPerDay
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">Do you drink Coffee?</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.coffeeConsumption}
                    onChange={(e) => handleInputChange("coffeeConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.coffeeConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.coffeeConsumption && (
                <input
                  type="text"
                  name="coffeeCupsPerDay"
                  value={dietaryData.coffeeCupsPerDay}
                  onChange={(e) => handleInputChange("coffeeCupsPerDay", e.target.value)}
                  placeholder="How many cups daily?"
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.coffeeCupsPerDay
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">Do you drink Soft (Aerated) Drinks?</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.softDrinksConsumption}
                    onChange={(e) => handleInputChange("softDrinksConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.softDrinksConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.softDrinksConsumption && (
                <select
                  name="softDrinksFrequency"
                  value={dietaryData.softDrinksFrequency}
                  onChange={(e) => handleInputChange("softDrinksFrequency", e.target.value)}
                  className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 mt-2 ${formErrors.softDrinksFrequency
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                >
                  <option value="" disabled>How frequently?</option>
                  {frequencyOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Cooking & Sweets */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sun size={18} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Cooking & Sweets
              </h3>
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Do you enjoy sweets / sugary food? <span className="text-red-500">*</span>
              </label>
              <select
                name="sweetsConsumption"
                value={dietaryData.sweetsConsumption}
                onChange={(e) => handleInputChange("sweetsConsumption", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.sweetsConsumption
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
              >
                <option value="" disabled>Select</option>
                {sweetsOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Which cooking oil is generally used at home? <span className="text-red-500">*</span>
              </label>
              <select
                name="cookingOil"
                value={dietaryData.cookingOil}
                onChange={(e) => handleInputChange("cookingOil", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.cookingOil
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
              >
                <option value="" disabled>Select</option>
                {cookingOilOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Any packaged foods consumed daily?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.packagedFoodsConsumption}
                    onChange={(e) => handleInputChange("packagedFoodsConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.packagedFoodsConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.packagedFoodsConsumption && (
                <textarea
                  name="packagedFoodsDetails"
                  value={dietaryData.packagedFoodsDetails}
                  onChange={(e) => handleInputChange("packagedFoodsDetails", e.target.value)}
                  rows="2"
                  placeholder="What do you eat? (e.g., protein bar or biscuit)"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.packagedFoodsDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Weekend eating pattern different?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dietaryData.weekendEatingPattern}
                    onChange={(e) => handleInputChange("weekendEatingPattern", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {dietaryData.weekendEatingPattern ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              {dietaryData.weekendEatingPattern && (
                <textarea
                  name="weekendEatingDetails"
                  value={dietaryData.weekendEatingDetails}
                  onChange={(e) => handleInputChange("weekendEatingDetails", e.target.value)}
                  rows="2"
                  placeholder="How is it different? (e.g., dine out on Saturdays)"
                  className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 mt-2 ${formErrors.weekendEatingDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                />
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <h3 className="text-[15px] font-semibold text-[#24312d] mb-2">
              Save Preferences
            </h3>

            <p className="text-[12px] text-[#98a19b] leading-5 mb-5">
              Updating your dietary preferences will recalculate your upcoming
              meal recommendations and grocery lists.
            </p>

            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="w-full h-[42px] bg-[#57b26a] text-white rounded-lg text-sm flex items-center justify-center gap-2 mb-3 hover:bg-[#4a9e5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={15} />
                  Save Changes
                </>
              )}
            </button>

            <button
              onClick={handleDiscard}
              disabled={isSaving}
              className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-sm text-[#4d5954] flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RotateCcw size={15} />
              Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DietaryPreferences;